import { createSignal, createEffect, onCleanup, Show, For } from 'solid-js';
import { useParams, useNavigate } from '@solidjs/router';
import { selfServiceApi, LeaderboardEntry, LeaderboardResponse, GameTemplateInfo } from '../services/selfservice';
import SolariRow from './SolariRow';

export default function SolariBoardPage() {
  const params = useParams<{ templateId?: string }>();
  const navigate = useNavigate();

  const [templates, setTemplates] = createSignal<GameTemplateInfo[]>([]);
  const [data, setData] = createSignal<LeaderboardResponse | null>(null);
  const [loading, setLoading] = createSignal(true);
  const [sessionType, setSessionType] = createSignal<'all' | 'solo' | 'group'>('all');
  const [error, setError] = createSignal<string | null>(null);

  // If no templateId, fetch templates and redirect
  createEffect(async () => {
    if (!params.templateId) {
      try {
        const res = await selfServiceApi.getTemplates();
        // Only show Polish versions on public ranking
        const plTemplates = res.templates.filter(t => !t.name.startsWith('Coded '));
        setTemplates(plTemplates);
        if (plTemplates.length === 1) {
          navigate(`/ranking/${plTemplates[0].id}`, { replace: true });
        } else if (plTemplates.length > 1) {
          setLoading(false);
        } else {
          setError('Brak aktywnych gier');
          setLoading(false);
        }
      } catch {
        setError('Nie udalo sie zaladowac danych');
        setLoading(false);
      }
    }
  });

  // Fetch leaderboard when templateId is set
  const fetchLeaderboard = async () => {
    if (!params.templateId) return;
    try {
      setLoading(true);
      const res = await selfServiceApi.getLeaderboard(
        parseInt(params.templateId),
        {
          sessionType: sessionType(),
          limit: 20,
        }
      );
      setData(res);
      setError(null);
    } catch {
      setError('Nie udalo sie zaladowac rankingu');
    } finally {
      setLoading(false);
    }
  };

  createEffect(() => {
    if (params.templateId) {
      fetchLeaderboard();
    }
  });

  // Auto-refresh every 60s
  const interval = setInterval(() => {
    if (params.templateId) {
      fetchLeaderboard();
    }
  }, 60000);
  onCleanup(() => clearInterval(interval));

  const handleFilterChange = (type: 'all' | 'solo' | 'group') => {
    setSessionType(type);
  };

  // Get leader's time in seconds for diff calculation
  const leaderTimeSeconds = () => {
    const ranking = data()?.ranking;
    if (ranking && ranking.length > 0) {
      return ranking[0].total_time_seconds;
    }
    return 0;
  };

  // Template selection view
  const TemplateSelector = () => (
    <div class="solari-page">
      <div class="solari-board-wrapper">
        <h1 class="solari-title">RANKING GLOBALNY</h1>
        <p class="solari-subtitle">Wybierz miasto</p>
        <div class="solari-template-list">
          <For each={templates()}>
            {(template) => (
              <button
                onClick={() => navigate(`/ranking/${template.id}`)}
                class="solari-template-btn"
              >
                <span class="solari-template-name">{template.name}</span>
                <span class="solari-template-location">{template.location}</span>
              </button>
            )}
          </For>
        </div>
      </div>
    </div>
  );

  // Main board view
  const Board = () => (
    <div class="solari-page">
      <div class="solari-board-wrapper">
        {/* Header */}
        <div class="solari-header">
          <h1 class="solari-title">{data()?.template_name?.toUpperCase() || 'RANKING'}</h1>
          <div class="solari-stats">
            <Show when={data()}>
              <span class="solari-stat">
                {data()?.total_entries} {(data()?.total_entries || 0) === 1 ? 'druzyna' : 'druzyn'}
              </span>
              <Show when={data()?.ranking && data()!.ranking.length > 0}>
                <span class="solari-stat-sep">|</span>
                <span class="solari-stat">
                  Najlepszy: {data()!.ranking[0]?.total_time}
                </span>
              </Show>
            </Show>
          </div>
        </div>

        {/* Board */}
        <div class="solari-board">
          {/* Table header */}
          <div class="solari-row-header">
            <div class="solari-pos">#</div>
            <div class="solari-name">ZESPOL</div>
            <div class="solari-time">CZAS</div>
            <div class="solari-diff">ROZNICA</div>
          </div>

          <div class="solari-divider" />

          {/* Data rows */}
          <Show
            when={!loading() || data()}
            fallback={
              <div class="solari-loading">
                <div class="solari-loading-text">LADOWANIE...</div>
              </div>
            }
          >
            <Show
              when={data()?.ranking && data()!.ranking.length > 0}
              fallback={
                <div class="solari-empty">BRAK WYNIKOW</div>
              }
            >
              <For each={data()?.ranking}>
                {(entry, index) => (
                  <SolariRow
                    position={entry.position}
                    teamName={entry.team_name}
                    time={entry.total_time}
                    timeDiffSeconds={entry.position >= 2 ? entry.total_time_seconds - leaderTimeSeconds() : 0}
                    animationDelay={index() * 100}
                    isTop3={entry.position <= 3}
                  />
                )}
              </For>
            </Show>
          </Show>

          {/* Empty rows to fill the board */}
          <Show when={data()?.ranking && data()!.ranking.length > 0 && data()!.ranking.length < 10}>
            <For each={Array.from({ length: 10 - (data()?.ranking?.length || 0) })}>
              {() => (
                <div class="solari-row solari-row-empty">
                  <div class="solari-field solari-pos">
                    <span class="solari-char-placeholder" />
                    <span class="solari-char-placeholder" />
                  </div>
                  <div class="solari-field solari-name">
                    {Array.from({ length: 20 }).map(() => (
                      <span class="solari-char-placeholder" />
                    ))}
                  </div>
                  <div class="solari-field solari-time">
                    {Array.from({ length: 8 }).map(() => (
                      <span class="solari-char-placeholder" />
                    ))}
                  </div>
                  <div class="solari-field solari-diff">
                    {Array.from({ length: 8 }).map(() => (
                      <span class="solari-char-placeholder" />
                    ))}
                  </div>
                </div>
              )}
            </For>
          </Show>
        </div>

        {/* Footer */}
        <div class="solari-footer">
          <span>za.graj.my</span>
          <Show when={loading() && data()}>
            <span class="solari-refreshing">odswiezanie...</span>
          </Show>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Show when={error()}>
        <div class="solari-page">
          <div class="solari-board-wrapper">
            <div class="solari-error">{error()}</div>
          </div>
        </div>
      </Show>
      <Show when={!error() && !params.templateId && templates().length > 1}>
        <TemplateSelector />
      </Show>
      <Show when={!error() && params.templateId}>
        <Board />
      </Show>
      <Show when={!error() && loading() && !params.templateId && templates().length === 0}>
        <div class="solari-page">
          <div class="solari-loading">
            <div class="solari-loading-text">LADOWANIE...</div>
          </div>
        </div>
      </Show>
    </>
  );
}
